import {FormControl} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {not} from 'ramda';

import {useGetTireSetQuery} from '@dms/api/core';

import {Nullish} from 'shared/web';

import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';
import {RequestTireSetFields} from './RequestTireSetFields';
import {TireSetFormHeader} from './TireSetFormHeader';
import {WheelArray} from './WheelArray';

interface TiresTabProps {
  isDraft: boolean;
  control: FormControl<TireSetDetailFormDefinition>;
  formApi: UseFormReturn<TireSetDetailFormDefinition>;
  tireOrderId: string;
  tireSetId?: string;
  homeBranchId: string | Nullish;
  onContactInformationChange: (
    contactInformation: TireSetDetailFormDefinition['contactInformation']
  ) => void;
}

const cloneContactInformation = (
  contactInformation: TireSetDetailFormDefinition['contactInformation']
) => {
  if (!contactInformation) {
    return null;
  }

  return {
    ...contactInformation,
    phoneNumber: contactInformation.phoneNumber ? {...contactInformation.phoneNumber} : null,
  };
};

export function TiresTab(props: TiresTabProps) {
  const formApi = props.formApi;
  const onContactInformationChange = props.onContactInformationChange;
  const tireSetQuery = useGetTireSetQuery(
    {
      orderId: props.tireOrderId,
      setId: props.tireSetId as string,
    },
    {skip: props.isDraft, refetchOnMountOrArgChange: true}
  );

  useEffect(() => {
    onContactInformationChange(
      cloneContactInformation(formApi.getValues('contactInformation') ?? null)
    );

    const subscription = formApi.watch((formValues) => {
      onContactInformationChange(cloneContactInformation(formValues.contactInformation ?? null));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [formApi, onContactInformationChange]);

  return (
    <Box
      width="100%"
      maxWidth={348}
      backgroundColor="general.white"
      borderRadius="small"
      padding={4}
    >
      <VStack spacing={4}>
        <TireSetFormHeader
          control={props.control}
          homeBranchId={props.homeBranchId}
          formApi={props.formApi}
          tireOrderId={props.tireOrderId}
          tireSetId={props.tireSetId}
          minNumberOfWheels={
            tireSetQuery.data?.wheels?.filter((wheel) => wheel?.state !== 'ARCHIVED')?.length
          }
          tireSetState={tireSetQuery.data?.state}
        />
        <WheelArray
          control={props.control}
          formApi={props.formApi}
          existingWheels={tireSetQuery.data?.wheels}
          homeBranchId={props.homeBranchId}
        />
        <RequestTireSetFields
          control={props.control}
          formApi={props.formApi}
          tireOrderId={props.tireOrderId}
          tireSetId={props.tireSetId}
          isEdit={not(props.isDraft)}
          tireSetState={tireSetQuery.data?.state}
          isDraft={props.isDraft}
          homeBranchId={props.homeBranchId}
        />
      </VStack>
    </Box>
  );
}
